import { z } from 'zod';
import {
  enrichableFieldSchema,
  type EnrichableField,
  type runKindSchema,
  type runStatusSchema,
} from '../domain/enums';

export const startIngestionSchema = z.object({
  /** Ingerir só estes produtos. Vazio significa varrer o catálogo inteiro (ou filtrar por categoryIds). */
  productIds: z.array(z.number().int().positive()).max(5000).default([]),
  /**
   * Ingerir produtos destas categorias VTEX (inclui subcategorias no worker).
   * Ignorado quando `productIds` não está vazio. Vazio + productIds vazio = catálogo inteiro.
   */
  categoryIds: z.array(z.number().int().positive()).max(50).default([]),
  /** Teto de produtos por execução, para não estourar rate limit em catálogo grande. */
  limit: z.number().int().min(1).max(100000).optional(),
  /** Reingerir mesmo quando o snapshot ainda está fresco. */
  force: z.boolean().default(false),
});
export type StartIngestion = z.infer<typeof startIngestionSchema>;

export const startAuditSchema = z.object({
  productIds: z.array(z.number().int().positive()).max(5000).default([]),
});
export type StartAudit = z.infer<typeof startAuditSchema>;

export const startGenerationSchema = z.object({
  productIds: z.array(z.number().int().positive()).max(1000).default([]),
  fields: z.array(enrichableFieldSchema).min(1),
  /** Gerar só para produtos que têm finding no campo. */
  onlyWithFindings: z.boolean().default(true),
  limit: z.number().int().min(1).max(1000).default(50),
});
export type StartGeneration = z.infer<typeof startGenerationSchema>;

export const startWriteSchema = z.object({
  /** Vazio significa "todas as sugestões aprovadas do tenant". */
  suggestionIds: z.array(z.string().uuid()).max(2000).default([]),
  limit: z.number().int().min(1).max(1000).optional(),
});
export type StartWrite = z.infer<typeof startWriteSchema>;

/** Normaliza `q` de busca dos previews (trim; vazio → undefined). */
function preprocessPreviewSearch(value: unknown): unknown {
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Query do preview de escrita: quantos produtos únicos entrariam no limite. */
export const writePreviewQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(1000).optional(),
  /** Busca por nome, productId ou skuId (só sobre sugestões aprovadas). */
  q: z.preprocess(preprocessPreviewSearch, z.string().max(200).optional()),
});
export type WritePreviewQuery = z.infer<typeof writePreviewQuerySchema>;

export interface WritePreviewProduct {
  productId: number;
  productName?: string;
  imageUrl?: string | null;
  suggestionIds: string[];
  fields: string[];
  batchCount: number;
}

export interface WritePreview {
  eligibleProductCount: number;
  eligibleSuggestionCount: number;
  previewProductCount: number;
  products: WritePreviewProduct[];
}

/** Query do preview de geração: campos, findings e limite da execução. */
function preprocessFieldsList(value: unknown): unknown {
  if (value === undefined || value === null || value === '') return undefined;
  const parts = Array.isArray(value)
    ? value.flatMap((item) => String(item).split(','))
    : String(value).split(',');
  const fields = parts.map((item) => item.trim()).filter(Boolean);
  return fields.length > 0 ? fields : undefined;
}

export const generationPreviewQuerySchema = z.object({
  fields: z.preprocess(preprocessFieldsList, z.array(enrichableFieldSchema).min(1)),
  onlyWithFindings: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional()
    .default(true),
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  /** Busca por nome, productId ou skuId entre elegíveis à geração. */
  q: z.preprocess(preprocessPreviewSearch, z.string().max(200).optional()),
});
/** Inferência de `preprocess` alarga o tipo; forçamos a saída esperada. */
export type GenerationPreviewQuery = Omit<
  z.infer<typeof generationPreviewQuerySchema>,
  'fields' | 'onlyWithFindings' | 'q'
> & {
  fields: EnrichableField[];
  onlyWithFindings: boolean;
  q?: string;
};

export interface GenerationPreviewProduct {
  productId: number;
  productName?: string;
  imageUrl?: string | null;
  score: number | null;
  fields: string[];
}

export interface GenerationPreview {
  eligibleProductCount: number;
  previewProductCount: number;
  products: GenerationPreviewProduct[];
  /**
   * Produtos com finding `specifications` mas zero alvos geráveis
   * (ex.: só Combo/SKU sem allowedValues / Text já preenchido).
   */
  specsFindingsWithoutTargets: number;
}

export interface RunSummary {
  id: string;
  tenantId: string;
  kind: z.infer<typeof runKindSchema>;
  status: z.infer<typeof runStatusSchema>;
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  skipped: number;
  dryRun: boolean;
  error: string | null;
  params: Record<string, unknown>;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface WriteOperationItemSummary {
  field: string;
  appliedValue: string;
}

export interface WriteOperationSummary {
  id: string;
  tenantId: string;
  runId: string | null;
  productId: number;
  skuId: number | null;
  status: string;
  dryRun: boolean;
  fields: string[];
  preWriteSnapshot: unknown;
  requestPayload: unknown;
  responseBody: unknown;
  httpStatus: number | null;
  error: string | null;
  rolledBackAt: string | null;
  indexedCheck: unknown;
  scoreBefore: number | null;
  scoreAfter: number | null;
  items: WriteOperationItemSummary[];
  createdAt: string;
}

/** Contagens por status dentro de um grupo de publicações do mesmo produto. */
export interface WriteOperationStatusCounts {
  succeeded: number;
  failed: number;
  pending: number;
  dry_run: number;
  aborted_conflict: number;
  rolled_back: number;
  other: number;
}

/**
 * Agrupamento visual da aba Publicações: uma linha-pai por productId,
 * com as WriteOperations embutidas. Rollback continua por `operations[].id`.
 */
export interface WriteOperationProductGroup {
  productId: number;
  productName: string | null;
  latestCreatedAt: string;
  fields: string[];
  statusCounts: WriteOperationStatusCounts;
  operations: WriteOperationSummary[];
}
